import React from 'react'
import { Mail } from 'lucide-react'
import type { ChatMessage } from '../../types'
import type {
  EmailMessageReceivedEventData,
  EmailMessageSentEventData,
} from '@super/shared-types'
import { formatMessageTime } from '@/utils/date-formatters'
import { TimeDisplay } from '@/components/common/TimeDisplay'
import { Avatar } from '@/components/common/Avatar'
import { MessageActionsMenu } from '../MessageActionsMenu'

interface TextMessageProps {
  message: ChatMessage
  isOutgoing: boolean
  threadId?: string
  companyId?: string
  sessionId?: string
  onSessionDetailsClick?: (sessionId: string) => void
  onEmailClick?: (
    emailData: EmailMessageReceivedEventData | EmailMessageSentEventData,
  ) => void
}

export const TextMessage: React.FC<TextMessageProps> = ({
  message,
  isOutgoing,
  threadId,
  companyId,
  sessionId,
  onSessionDetailsClick,
  onEmailClick,
}) => {
  const isAgent = message.sender.id === 'assistant'
  const shouldAlignRight = isOutgoing || isAgent

  // Check if this is an email message
  const isEmail = message.sender.type === 'email'
  const emailData = isEmail
    ? (message.data as
        | EmailMessageReceivedEventData
        | EmailMessageSentEventData)
    : null

  const handleClick = () => {
    if (isEmail && emailData && onEmailClick) {
      onEmailClick(emailData)
    }
  }

  return (
    <div
      className={`mb-4 flex items-start gap-2 ${shouldAlignRight ? 'flex-row-reverse' : 'flex-row'} ${message.isLoading ? 'opacity-60' : ''} group relative`}
    >
      {!shouldAlignRight && (
        <Avatar
          email={isEmail ? emailData?.from : undefined}
          name={message.sender.name}
          avatarUrl={message.sender.avatar}
          size={32}
        />
      )}

      <div
        className={`max-w-[70%] flex flex-col gap-1 ${shouldAlignRight ? 'items-end' : 'items-start'}`}
      >
        {!shouldAlignRight && (
          <div className="text-xs font-semibold text-gray-900 dark:text-foreground mb-0.5">
            {message.sender.name}
          </div>
        )}

        <div className="flex items-end gap-2">
          <div
            className={`px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-break-spaces relative group/message ${
              isOutgoing
                ? 'bg-gray-900 text-white rounded-br-sm dark:bg-gray-800'
                : isAgent
                  ? 'bg-[#EBF6FF] text-gray-900 rounded-br-sm dark:bg-info-subtle dark:text-foreground'
                  : 'bg-gray-100 text-gray-900 rounded-bl-sm dark:bg-muted dark:text-foreground'
            } ${isEmail ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onClick={handleClick}
          >
            {isEmail && (
              <div className="flex items-center gap-1.5 mb-1 text-xs font-medium opacity-70">
                <Mail className="w-3 h-3" />
                <span>{emailData?.subject || '(No Subject)'}</span>
              </div>
            )}
            {threadId && (
              <div
                className={`absolute ${shouldAlignRight ? '-left-10 top-1' : '-right-10 -top-1'} z-30`}
              >
                <MessageActionsMenu
                  threadId={threadId}
                  messageId={message.id}
                  companyId={companyId}
                  sessionId={sessionId}
                  onSessionDetailsClick={onSessionDetailsClick}
                  tooltipSide={shouldAlignRight ? 'left' : 'right'}
                />
              </div>
            )}
            {message.content}
          </div>
          <TimeDisplay
            date={message.createdAt}
            formatter={formatMessageTime}
            className="text-xs text-gray-500 dark:text-muted-foreground whitespace-nowrap self-end px-1"
          />
        </div>
      </div>
    </div>
  )
}
